/**
 * Cart / order line identity (client side).
 *
 * A line is `{ pid, sku, choices, choiceLabels, components, lineKey, quantity,
 * options, ... }`. `options` is the free-form per-line data a project keeps
 * (platform distribution, seller, …); what the buyer chose on the product page
 * travels as `choices` (and `components` for an outfit), so the two never
 * collide.
 *
 * The server keeps the same helper (aldoo-na/store/lines.js) with the same
 * algorithm: a line's key is its pid when it carries no sku, choices or
 * components — every line written before the selection existed keeps its key —
 * otherwise `pid|sku|stableStringify(choices)|stableStringify(componentIdentity(components))`:
 * an outfit's parts count by what identifies them alone (`{ key, pid, choices }`,
 * sorted by key), so the line the client adds and the line the server resolves
 * (its parts carry a sku, an amount and labels in the order's language) have
 * one key.
 */

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value)

/**
 * JSON with the keys of every object sorted, so two equal selections give one
 * string whatever order they were built in. Arrays keep their order;
 * `undefined` and functions are dropped as JSON.stringify drops them.
 */
export const stableStringify = (value) => {
  if (value === undefined || typeof value === "function") return undefined
  if (value === null || typeof value !== "object") return JSON.stringify(value)
  if (typeof value.toJSON === "function") return stableStringify(value.toJSON())
  if (Array.isArray(value)) return `[${value.map((entry) => stableStringify(entry) ?? "null").join(",")}]`
  const entries = Object.keys(value)
    .sort()
    .map((key) => [key, stableStringify(value[key])])
    .filter(([, encoded]) => encoded !== undefined)
  return `{${entries.map(([key, encoded]) => `${JSON.stringify(key)}:${encoded}`).join(",")}}`
}

const hasChoices = (line) => isPlainObject(line?.choices) && Object.keys(line.choices).length > 0
const hasComponents = (line) => Array.isArray(line?.components) && line.components.length > 0

/** Does the line carry a buyer's selection (a sku, choices or components)? */
export const hasSelection = (line) => !!line?.sku || hasChoices(line) || hasComponents(line)

/**
 * What of an outfit's parts makes a line's identity: `[{ key, pid, choices }]`
 * sorted by key (code unit order) — a part's sku, qty, amount and labels are
 * left out, as the server leaves them out.
 */
export const componentIdentity = (components) =>
  (Array.isArray(components) ? components : [])
    .filter(isPlainObject)
    .map((component) => ({ key: String(component.key ?? ""), pid: String(component.pid ?? ""), choices: isPlainObject(component.choices) ? component.choices : {} }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))

/**
 * The line's identity. A plain line keeps the key the framework cart always
 * gave it (`product_id || pid`, the pid wherever both are set); a line with a
 * selection is keyed by its pid, as the server keys it.
 */
export const lineKey = (line) => {
  if (!hasSelection(line)) return line?.product_id || line?.pid
  const pid = line.pid || line.product_id || ""
  const choices = isPlainObject(line.choices) ? line.choices : {}
  return [pid, line.sku || "", stableStringify(choices), stableStringify(componentIdentity(line.components))].join("|")
}

/**
 * The line with a selection applied: `sku`, `choices`, `choiceLabels` and
 * `components` are taken from the selection (only the ones it has), and the
 * line records its key. A selection with nothing in it leaves the line as it
 * was, so a product without extensions adds exactly the line it always did.
 */
export const withSelection = (line, selection) => {
  if (!selection || !hasSelection(selection)) return line
  const next = { ...line }
  if (selection.sku) next.sku = selection.sku
  if (hasChoices(selection)) next.choices = selection.choices
  if (isPlainObject(selection.choiceLabels) && Object.keys(selection.choiceLabels).length) next.choiceLabels = selection.choiceLabels
  if (hasComponents(selection)) next.components = selection.components
  next.lineKey = lineKey(next)
  return next
}

/** The selection fields of a line, for a request to the server (empty ones left out). */
export const selectionOf = (line) => {
  if (!hasSelection(line)) return {}
  return {
    ...(line.sku ? { sku: line.sku } : {}),
    ...(hasChoices(line) ? { choices: line.choices } : {}),
    ...(isPlainObject(line.choiceLabels) && Object.keys(line.choiceLabels).length ? { choiceLabels: line.choiceLabels } : {}),
    ...(hasComponents(line) ? { components: line.components } : {}),
    lineKey: lineKey(line),
  }
}

/** "M" or "Size: M" — one choice as a buyer reads it (choiceLabels entry). */
export const choiceText = (entry) => {
  if (isPlainObject(entry)) return entry.label ? `${entry.label}: ${entry.value ?? ""}` : String(entry.value ?? "")
  return entry === undefined || entry === null ? "" : String(entry)
}

/** What was chosen on a line itself: ["Size: M"]. */
export const lineChoices = (line) => Object.values(line?.choiceLabels || {}).map(choiceText).filter(Boolean)

/** An outfit's parts as a buyer reads them: ["T-shirt Size: M", "Shorts Size: L"]. */
export const lineParts = (line) =>
  (Array.isArray(line?.components) ? line.components : []).map((part) => [part.label || part.pid, Object.values(part.choiceLabels || {}).map(choiceText).filter(Boolean).join(", ")].filter(Boolean).join(" ")).filter(Boolean)

/**
 * A line's name with what was chosen, as the letters name it
 * (aldoo-na/store/lines describe): `T-shirt (Size: M)`, an outfit
 * `Outfit (Top Size: M; Shorts Size: L)`. A plain line is its name.
 */
export const describeLine = (line, name = line?.name || line?.pid) => {
  const own = lineChoices(line)
  const said = [...(own.length ? [own.join(", ")] : []), ...lineParts(line)]
  return said.length ? `${name} (${said.join("; ")})` : name
}
